(() => {
  // 20 s: on first consent accept, the chain is: CookiebotOnConsentReady →
  // setTrackingConsent → visitorConsentCollected → Swym re-check →
  // loadSwymShopifyScript → CDN fetch → _swat ready. 8 s was too short for
  // slow connections; 20 s covers the full async path without being noticeable.
  const SWYM_LOAD_TIMEOUT_MS = 20000;
  const HOST_SELECTOR = "wishlist-button";
  const ADDED_CLASS = "swym-added";
  const LOADED_CLASS = "swym-loaded";
  const CARD_VARIANT_CONTROL_SELECTOR =
    '.product-card__swatch-list [type="radio"], .product-card__variant-list [type="radio"]';
  const CARD_SELECTED_VARIANT_SELECTOR =
    '.product-card__swatch-list [type="radio"][data-variant-id]:checked, .product-card__variant-list [type="radio"][data-variant-id]:checked';
  const CARD_SELECTED_CONTROL_SELECTOR =
    '.product-card__swatch-list [type="radio"]:checked, .product-card__variant-list [type="radio"]:checked';
  const TOAST_ELEMENT_ID = "jkl-wishlist-toast";
  const TOAST_VISIBLE_CLASS = "is-visible";
  const TOAST_DURATION_MS = 2600;
  const HEADER_WISHLIST_COUNT_SELECTOR = "[data-jkl-wishlist-count]";

  let swymReadyPromise;
  let didWarnMissingSwym = false;
  let hostObserver = null;

  const hostControllers = new WeakMap();
  const boundHosts = new Set();

  const createWishlistToast = () => {
    let hideTimerId = null;

    const getToastElement = () => {
      let toast = document.getElementById(TOAST_ELEMENT_ID);
      if (toast) {
        return toast;
      }

      toast = document.createElement("div");
      toast.id = TOAST_ELEMENT_ID;
      toast.className = "jkl-wishlist-toast";
      toast.setAttribute("role", "status");
      toast.setAttribute("aria-live", "polite");
      document.body.appendChild(toast);
      return toast;
    };

    const show = (message, modifier = "success") => {
      if (typeof message !== "string" || !message.trim()) {
        return;
      }

      const toast = getToastElement();
      toast.textContent = message;
      toast.classList.remove(
        "jkl-wishlist-toast--success",
        "jkl-wishlist-toast--error",
      );
      toast.classList.add(`jkl-wishlist-toast--${modifier}`);

      toast.classList.remove(TOAST_VISIBLE_CLASS);
      window.requestAnimationFrame(() => {
        toast.classList.add(TOAST_VISIBLE_CLASS);
      });

      if (hideTimerId !== null) {
        window.clearTimeout(hideTimerId);
      }

      hideTimerId = window.setTimeout(() => {
        toast.classList.remove(TOAST_VISIBLE_CLASS);
      }, TOAST_DURATION_MS);
    };

    const destroy = () => {
      if (hideTimerId !== null) {
        window.clearTimeout(hideTimerId);
        hideTimerId = null;
      }
    };

    return Object.freeze({
      show,
      destroy,
    });
  };

  const wishlistToast = window.JKLWishlistToast || createWishlistToast();
  window.JKLWishlistToast = wishlistToast;

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const readBodyString = (key) => {
    const value = document.body?.dataset?.[key];
    return typeof value === "string" ? value.trim() : "";
  };

  const wishlistStrings = Object.freeze({
    addedToastText: readBodyString("jklWishlistToastAddedText"),
    removedToastText: readBodyString("jklWishlistToastRemovedText"),
  });
  window.JKLWishlistI18n = Object.freeze({
    ...(window.JKLWishlistI18n || {}),
    ...wishlistStrings,
  });

  const normalizeImageSrc = (value) => {
    if (typeof value !== "string" || !value) {
      return "";
    }

    if (value.startsWith("//")) {
      return `${window.location.protocol}${value}`;
    }

    return value;
  };

  const getEventType = (swat) =>
    window.SwymTracker?.EventTypes?.addToWishList ||
    swat?.EventTypes?.addToWishList ||
    4;

  const setButtonState = (button, isAdded) => {
    if (!button) {
      return;
    }

    button.classList.toggle(ADDED_CLASS, isAdded);
    button.setAttribute("aria-pressed", isAdded ? "true" : "false");
  };

  const withSwymCallback = (executor) =>
    new Promise((resolve, reject) => {
      try {
        executor(resolve, reject);
      } catch (error) {
        reject(error);
      }
    });

  const fetchWishlistItems = (swat) =>
    new Promise((resolve) => {
      if (!swat || typeof swat.fetch !== "function") {
        resolve([]);
        return;
      }

      try {
        swat.fetch((products) => {
          resolve(Array.isArray(products) ? products : []);
        });
      } catch {
        resolve([]);
      }
    });

  const setHeaderWishlistCount = (count) => {
    const safeCount = Math.max(0, Number(count) || 0);

    document
      .querySelectorAll(HEADER_WISHLIST_COUNT_SELECTOR)
      .forEach((node) => {
        const valueNode =
          node.querySelector("[data-jkl-wishlist-count-value]") || node;
        valueNode.textContent = String(safeCount);
        const shouldHide = safeCount === 0;
        node.hidden = shouldHide;
        node.setAttribute("aria-hidden", shouldHide ? "true" : "false");
        node.classList.toggle("opacity-0", shouldHide);
      });
  };

  const getCountableWishlistItems = (items, swat) => {
    const eventType = getEventType(swat);
    const itemsWithEventType = items.filter(
      (item) => toNumber(item?.et) === eventType,
    );

    if (itemsWithEventType.length > 0) {
      return itemsWithEventType;
    }

    return items;
  };

  const syncHeaderWishlistCount = async (swatOverride = null) => {
    const swat = swatOverride || (await getSwym().catch(() => null));
    if (!swat) {
      setHeaderWishlistCount(0);
      return;
    }

    const items = await fetchWishlistItems(swat);
    const countableItems = getCountableWishlistItems(items, swat);
    setHeaderWishlistCount(countableItems.length);
  };

  const getSwym = () => {
    if (swymReadyPromise) {
      return swymReadyPromise;
    }

    swymReadyPromise = new Promise((resolve, reject) => {
      if (window._swat) {
        resolve(window._swat);
        return;
      }

      const onReady = (swat) => resolve(swat || window._swat || null);
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push(onReady);

      window.setTimeout(() => {
        if (window._swat) {
          resolve(window._swat);
          return;
        }

        if (!didWarnMissingSwym) {
          didWarnMissingSwym = true;
          console.warn(
            "[JKL Wishlist] Swym (_swat) did not load; wishlist buttons remain hidden.",
          );
        }

        reject(new Error("Swym (_swat) did not load."));
      }, SWYM_LOAD_TIMEOUT_MS);
    });

    return swymReadyPromise;
  };

  const getHostForm = (host) => {
    const formRef = host.getAttribute("form");

    if (!formRef) {
      return null;
    }

    return document.forms[formRef] || null;
  };

  const getSelectedCardControl = (card) => {
    if (!card) {
      return null;
    }

    return (
      card.querySelector(CARD_SELECTED_VARIANT_SELECTOR) ||
      card.querySelector(CARD_SELECTED_CONTROL_SELECTOR)
    );
  };

  const getContext = (host, form) => {
    const pageData = window.SwymPageData || {};
    const card = host.closest("product-card");
    const selectedCardControl = getSelectedCardControl(card);
    const productId = toNumber(host.getAttribute("product-id"));
    const formVariantId = toNumber(
      form?.querySelector('input[name="id"]')?.value,
    );
    const cardVariantId = toNumber(
      selectedCardControl?.getAttribute("data-variant-id"),
    );
    const hostVariantId = toNumber(host.getAttribute("variant-id"));
    const variantId = formVariantId || cardVariantId || hostVariantId;
    const selectedCardProductUrl =
      selectedCardControl?.getAttribute("data-product-url") || "";
    const rawProductUrl =
      selectedCardProductUrl ||
      host.getAttribute("product-url") ||
      pageData.du ||
      `${window.location.origin}${window.location.pathname}`;
    const productUrl = rawProductUrl;
    const rawProductImageSrc = host.getAttribute("product-image-src") || "";
    const productImageSrc = normalizeImageSrc(
      rawProductImageSrc || pageData.iu || "",
    );
    const productTitle =
      host.getAttribute("product-title") || pageData.dt || "";

    return {
      pageData,
      productId,
      variantId,
      productUrl,
      rawProductUrl,
      productImageSrc,
      rawProductImageSrc,
      productTitle,
    };
  };

  const getProductWishlistItems = (items, swat, productId) => {
    const eventType = getEventType(swat);
    const itemsWithEventType = items.filter(
      (item) =>
        toNumber(item?.empi) === productId && toNumber(item?.et) === eventType,
    );

    if (itemsWithEventType.length > 0) {
      return itemsWithEventType;
    }

    return items.filter((item) => toNumber(item?.empi) === productId);
  };

  const buildPayload = (host, form, swat) => {
    const {
      pageData,
      productId,
      variantId,
      productUrl,
      rawProductUrl,
      productImageSrc,
      rawProductImageSrc,
      productTitle,
    } = getContext(host, form);

    if (!productId || !variantId || !productUrl) {
      return null;
    }

    return {
      ...pageData,
      et: getEventType(swat),
      dt: productTitle,
      empi: productId,
      epi: variantId,
      du: productUrl,
      iu: productImageSrc,
      cprops: {
        ...(pageData?.cprops || {}),
        jkl_product_url: rawProductUrl || productUrl,
        jkl_product_image_src: rawProductImageSrc || productImageSrc,
        jkl_product_title: productTitle,
      },
    };
  };

  const createWishlistController = (host) => {
    let destroyed = false;
    let busy = false;
    let syncToken = 0;
    let swat = null;

    const button = host.querySelector(".swym-button");
    const form = getHostForm(host);
    const card = host.closest("product-card");

    const refreshHostFallbackAttributes = () => {
      const selectedCardControl = getSelectedCardControl(card);
      if (!selectedCardControl) {
        return;
      }

      const variantId = toNumber(
        selectedCardControl.getAttribute("data-variant-id"),
      );
      if (variantId) {
        host.setAttribute("variant-id", String(variantId));
      }

      const productUrl = selectedCardControl.getAttribute("data-product-url");
      if (productUrl) {
        host.setAttribute("product-url", productUrl);
      }
    };

    const sync = async () => {
      if (destroyed || !button || !swat) {
        return;
      }

      const { productId } = getContext(host, form);
      if (!productId) {
        setButtonState(button, false);
        return;
      }

      const token = ++syncToken;
      const items = await fetchWishlistItems(swat);
      if (destroyed || token !== syncToken) {
        return;
      }

      const countableItems = getCountableWishlistItems(items, swat);
      setHeaderWishlistCount(countableItems.length);

      const productItems = getProductWishlistItems(items, swat, productId);
      setButtonState(button, productItems.length > 0);
    };

    const add = async () => {
      if (typeof swat?.addToWishList !== "function") {
        return;
      }

      const payload = buildPayload(host, form, swat);
      if (!payload) {
        return;
      }

      const didAdd = await withSwymCallback((resolve, reject) => {
        swat.addToWishList(
          payload,
          () => resolve(),
          (error) => reject(error),
        );
      })
        .then(() => true)
        .catch(() => false);

      if (!destroyed && didAdd) {
        console.log("[JKL Wishlist] Item added successfully.", {
          productId: payload.empi,
          variantId: payload.epi,
        });
        setButtonState(button, true);
        await syncHeaderWishlistCount(swat);
        if (wishlistStrings.addedToastText) {
          wishlistToast.show(wishlistStrings.addedToastText, "success");
        }

        host.dispatchEvent(
          new CustomEvent("wishlist:add", {
            bubbles: true,
            detail: {
              productId: payload.empi,
              variantId: payload.epi,
              productTitle: payload.dt,
              productUrl: payload.du,
            },
          }),
        );
      }
    };

    const removeAllForProduct = async () => {
      if (typeof swat?.removeFromWishList !== "function") {
        return;
      }

      const { productId } = getContext(host, form);
      if (!productId) {
        return;
      }

      const items = await fetchWishlistItems(swat);
      const productItems = getProductWishlistItems(items, swat, productId);

      if (productItems.length === 0) {
        setButtonState(button, false);
        return;
      }

      let hasRemoveFailure = false;
      for (const item of productItems) {
        const didRemove = await withSwymCallback((resolve, reject) => {
          swat.removeFromWishList(
            item,
            () => resolve(),
            (error) => reject(error),
          );
        })
          .then(() => true)
          .catch(() => false);

        if (!didRemove) {
          hasRemoveFailure = true;
        }
      }

      if (destroyed) {
        return;
      }

      if (hasRemoveFailure) {
        await sync();
      } else {
        setButtonState(button, false);
        await syncHeaderWishlistCount(swat);
        if (wishlistStrings.removedToastText) {
          wishlistToast.show(wishlistStrings.removedToastText, "success");
        }
      }
    };

    const toggle = async (event) => {
      event?.preventDefault();

      if (destroyed || !button || busy) {
        return;
      }

      swat = swat || (await getSwym().catch(() => null));
      if (!swat) {
        return;
      }

      busy = true;
      button.setAttribute("aria-busy", "true");

      try {
        if (button.classList.contains(ADDED_CLASS)) {
          await removeAllForProduct();
        } else {
          await add();
        }
      } finally {
        busy = false;
        button.removeAttribute("aria-busy");
      }
    };

    const onVariantChange = () => {
      refreshHostFallbackAttributes();
      window.requestAnimationFrame(sync);
    };

    const onCardVariantChange = (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const control = event.target.closest(CARD_VARIANT_CONTROL_SELECTOR);
      if (!control || !card?.contains(control)) {
        return;
      }

      refreshHostFallbackAttributes();
      window.requestAnimationFrame(sync);
    };

    const connect = async () => {
      if (destroyed || !button) {
        return;
      }

      button.addEventListener("click", toggle);
      form?.addEventListener("variant:change", onVariantChange);
      card?.addEventListener("change", onCardVariantChange);
      refreshHostFallbackAttributes();

      swat = await getSwym().catch(() => null);
      if (!swat || destroyed) {
        return;
      }

      await sync();
      if (!destroyed) {
        button.classList.add(LOADED_CLASS);
      }
    };

    const disconnect = () => {
      destroyed = true;
      syncToken += 1;
      button?.removeEventListener("click", toggle);
      form?.removeEventListener("variant:change", onVariantChange);
      card?.removeEventListener("change", onCardVariantChange);
    };

    return {
      connect,
      disconnect,
      sync,
      toggle,
    };
  };

  const bindHost = (host) => {
    if (!(host instanceof Element) || hostControllers.has(host)) {
      return hostControllers.get(host) || null;
    }

    const controller = createWishlistController(host);
    hostControllers.set(host, controller);
    boundHosts.add(host);
    controller.connect();
    return controller;
  };

  const unbindHost = (host) => {
    const controller = hostControllers.get(host);
    if (!controller) {
      return;
    }

    controller.disconnect();
    hostControllers.delete(host);
    boundHosts.delete(host);
  };

  const bindHostsIn = (root = document) => {
    if (!root) {
      return;
    }

    if (root instanceof Element && root.matches(HOST_SELECTOR)) {
      bindHost(root);
    }

    root.querySelectorAll?.(HOST_SELECTOR).forEach((host) => bindHost(host));
  };

  const unbindHostsIn = (root) => {
    if (!root) {
      return;
    }

    if (root instanceof Element && root.matches(HOST_SELECTOR)) {
      unbindHost(root);
    }

    root.querySelectorAll?.(HOST_SELECTOR).forEach((host) => unbindHost(host));
  };

  const ensureObserver = () => {
    if (hostObserver) {
      return;
    }

    hostObserver = new MutationObserver((mutationList) => {
      mutationList.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
          }
          bindHostsIn(node);
        });

        mutation.removedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
          }
          unbindHostsIn(node);
        });
      });
    });

    hostObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  const init = (root = document) => {
    bindHostsIn(root);
    ensureObserver();
    syncHeaderWishlistCount();
  };

  const sync = async (host) => {
    if (!host) {
      return;
    }

    const controller = hostControllers.get(host) || bindHost(host);
    await controller?.sync?.();
  };

  const syncAll = async (root = document) => {
    bindHostsIn(root);

    const syncTasks = [];
    if (root instanceof Element && root.matches(HOST_SELECTOR)) {
      const controller = hostControllers.get(root);
      if (controller) {
        syncTasks.push(controller.sync());
      }
    }

    root.querySelectorAll?.(HOST_SELECTOR).forEach((host) => {
      const controller = hostControllers.get(host);
      if (controller) {
        syncTasks.push(controller.sync());
      }
    });

    await Promise.all(syncTasks);
  };

  const destroy = () => {
    hostObserver?.disconnect();
    hostObserver = null;

    Array.from(boundHosts).forEach((host) => unbindHost(host));
  };

  if (window.JKLSwymWishlist?.destroy) {
    window.JKLSwymWishlist.destroy();
  }

  const WishlistLibrary = Object.freeze({
    init,
    sync,
    syncAll,
    syncHeaderCount: syncHeaderWishlistCount,
    destroy,
    getSwym,
  });

  window.JKLSwymWishlist = WishlistLibrary;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document), {
      once: true,
    });
  } else {
    init(document);
  }
})();
